import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useWheelbarrow } from '../context/WheelbarrowContext';
import './CardPayment.css';

declare global {
  interface Window {
    Square: any;
  }
}

const CardPayment: React.FC = () => {
  const navigate = useNavigate();
  const { state } = useWheelbarrow();
  const [paymentStatus, setPaymentStatus] = useState<'pending' | 'processing' | 'confirmed' | 'failed'>('pending');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);

  // Calculate total amount including service fee in GBP
  const subtotalGBP = state.totalPence / 100; // Convert pence to GBP
  const serviceFeeGBP = subtotalGBP * 0.1;
  const totalGBP = subtotalGBP + serviceFeeGBP;

  useEffect(() => {
    // Load Square Web Payments SDK
    const script = document.createElement('script');
    script.src = 'https://sandbox.web.squarecdn.com/v1/square.js';
    script.onload = initializeSquare;
    document.body.appendChild(script);

    return () => {
      document.body.removeChild(script);
    };
  }, []);

  const initializeSquare = async () => {
    if (!window.Square) {
      setErrorMessage('Failed to load payment system');
      setIsLoading(false);
      return;
    }

    try {
      const payments = window.Square.payments('YOUR_SQUARE_APPLICATION_ID', 'YOUR_LOCATION_ID');
      const card = await payments.card();
      await card.attach('#card-container');
      setIsLoading(false);
    } catch (error) {
      console.error('Failed to initialize Square:', error);
      setErrorMessage('Failed to initialize payment system');
      setIsLoading(false);
    }
  };

  const handlePayment = async () => {
    setPaymentStatus('processing');
    
    try {
      // TODO: Implement actual Square payment processing
      // For now, simulate a successful payment
      await new Promise(resolve => setTimeout(resolve, 2000));
      setPaymentStatus('confirmed');
      
      // Redirect to success page after payment
      setTimeout(() => {
        navigate('/payment-success');
      }, 2000);
      
    } catch (error) {
      setPaymentStatus('failed');
      setErrorMessage('Payment failed. Please try again.');
    }
  };

  return (
    <div className="card-payment-page">
      <div className="breadcrumb">
        <button onClick={() => navigate('/')} className="breadcrumb-item">
          Categories
        </button>
        <span className="breadcrumb-separator">›</span>
        <button onClick={() => navigate('/checkout')} className="breadcrumb-item">
          Checkout
        </button>
        <span className="breadcrumb-separator">›</span>
        <button onClick={() => navigate('/payment-selection')} className="breadcrumb-item">
          Payment Method
        </button>
        <span className="breadcrumb-separator">›</span>
        <span className="breadcrumb-item active">Card Payment</span>
      </div>

      <div className="payment-content">
        <h1>Pay with Card</h1>
        
        <div className="amount-display">
          <div className="amount-details">
            <h2>Amount Due</h2>
            <div className="amount gbp">£{totalGBP.toFixed(2)}</div>
            <div className="amount-breakdown">
              <div className="breakdown-item">
                <span>Subtotal:</span>
                <span>£{subtotalGBP.toFixed(2)}</span>
              </div>
              <div className="breakdown-item">
                <span>Service Fee:</span>
                <span>£{serviceFeeGBP.toFixed(2)}</span>
              </div>
            </div>
          </div>
        </div>

        {isLoading ? (
          <div className="loading-state">
            <p>Loading payment form...</p>
          </div>
        ) : errorMessage ? (
          <div className="error-state">
            <p>{errorMessage}</p>
            <button 
              className="retry-button"
              onClick={() => window.location.reload()}
            >
              Try Again
            </button>
          </div>
        ) : (
          <div className="payment-form">
            <div id="card-container"></div>
            
            <button 
              className="pay-button"
              onClick={handlePayment}
              disabled={paymentStatus === 'processing'}
            >
              {paymentStatus === 'processing' ? 'Processing...' : `Pay £${totalGBP.toFixed(2)}`}
            </button>

            {paymentStatus === 'confirmed' && (
              <div className="success-message">
                <p>✅ Payment successful!</p>
                <p>Redirecting...</p>
              </div>
            )}

            {paymentStatus === 'failed' && (
              <div className="error-message">
                <p>❌ {errorMessage}</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default CardPayment; 